import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import ContactEditModal from './ContactEditModal';
import ChangePasswordModal from './ChangePasswordModal';
import NewObjectModal from "./NewObjectModal";
import { LanguageContext } from "../context/LanguageContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const { language, texts } = useContext(LanguageContext);

  const [userData, setUserData] = useState(null);
  const [counts, setCounts] = useState({ active: 0, inReview: 0 });
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showNewObjectModal, setShowNewObjectModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    // 1) Userdaten laden
    fetch(`${process.env.REACT_APP_API_URL}/dashboard?lang=${language}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        const u = data.user || {};

        const street = u.street || '';
        const house_number = u.house_number || '';
        const addressLine1 = [street, house_number].filter(Boolean).join(' ').trim();

        const postal_code = u.postal_code || '';
        const town = u.town || u.city || '';

        // country_name kommt jetzt bereits korrekt übersetzt aus der API
        const countryName = u.country_name || '';

        const pcTown = [postal_code, town].filter(Boolean).join(' ').trim();

        const addressLine2Parts = [];
        if (pcTown) addressLine2Parts.push(pcTown);
        if (countryName) addressLine2Parts.push(countryName);

        const addressLine2 = addressLine2Parts.join(', ');

        setUserData({
          ...u,
          first_name: u.first_name || '',
          addressLine1,
          addressLine2,
          phone_number: u.phone_number || u.phone || u.telephone || '',
          email: u.email || '',
        });
      });


    // 2) Summary laden
    fetch(`${process.env.REACT_APP_API_URL}/api/dashboard/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        setCounts({
          active: Number(data.active_count || 0),
          inReview: Number(data.in_review_count || 0),
        });
      });

    // 3) Draft prüfen
    fetch(`${process.env.REACT_APP_API_URL}/api/glamping/draft/open`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.hasDraft) setDraft(data.draft_id);
      });

  }, [navigate, language]);

  const handleNewObjectClick = () => {
    if (!draft) {
      navigate('/create-object');
      return;
    }
    setShowNewObjectModal(true);
  };

  const deleteDraftAndStartNew = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/glamping/draft/${draft}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      navigate('/create-object');
    } catch (err) {
      console.error("Fehler beim Löschen des Drafts:", err);
    }
  };

return (
  <div className="dashboard-container">
    <header className="menu-bar">
      <h1>
        {userData
          ? `${texts.dashboard.welcome}${userData.first_name}`
          : texts.dashboard.welcome}
      </h1>
    </header>

    <section className="tile-container">

      {/* Übersicht */}
      <div className="tile tile-overview">
        <h2>{texts.dashboard.overview}</h2>

        <div className="tile-stats">
          {counts.active > 0 && (
            <div className="tile-stat">
              <span className="stat-number">{counts.active}</span>
              <span className="stat-label">
                {texts.dashboard.activeObjects}
              </span>
            </div>
          )}

          {counts.inReview > 0 && (
            <div className="tile-stat">
              <span className="stat-number">{counts.inReview}</span>
              <span className="stat-label">
                {texts.dashboard.inReviewObjects}
              </span>
            </div>
          )}
        </div>

        <div className="tile-divider"></div>

        <p className="tile-summary">
          {texts.dashboard.summaryText}
        </p>
      </div>


      {/* Kontaktdaten */}
      <div className="tile tile-contact">
        <h2>{texts.dashboard.contact}</h2>

        <div className="contact-info">
          <p className="contact-line">{userData?.addressLine1 || '—'}</p>
          <p className="contact-line">{userData?.addressLine2 || '—'}</p>

          <div className="tile-divider"></div>

          <p className="contact-line">{userData?.phone_number || '—'}</p>
          <p className="contact-line">{userData?.email || '—'}</p>
        </div>

        <button onClick={() => setShowModal(true)} className="contact-edit-btn">
          {texts.dashboard.editContact}
        </button>
      </div>

      {/* Einstellungen */}
      <div className="tile tile-settings">
        <h2>{texts.dashboard.settings}</h2>

        <div className="settings-list">
          <div className="settings-item">
            <span className="settings-label">{texts.dashboard.subscription}</span>
            <span className="settings-value">{texts.dashboard.subscriptionActive}</span>
          </div>

          <div className="tile-divider"></div>

          <div className="settings-item">
            <span className="settings-label">{texts.dashboard.password}</span>
            <button
              className="settings-btn"
              onClick={() => setShowPasswordModal(true)}
            >
              {texts.dashboard.changePassword}
            </button>
          </div>
        </div>
      </div>

      {/* Unterkünfte */}
      <div className="tile tile-objects">
        <h2>{texts.dashboard.objects}</h2>

        <div className="objects-info">
          <p className="objects-line">{texts.dashboard.objectboxText}</p>
          <p className="objects-line">{texts.dashboard.objectboxText2}</p>
        </div>

        <div className="tile-divider"></div>

        <button
          className="objects-btn"
          onClick={() => navigate('/object-list')}
        >
          {texts.dashboard.objectbuttonText}
        </button>
      </div>

      {/* Neue Unterkunft */}
      <div className="tile tile-new-object">
        <h2>{texts.dashboard.newObjectContainer}</h2>

        <p className="new-object-description">
          {texts.dashboard.newObjectDescription}
        </p>

        <div className="tile-divider"></div>

        <div className="new-object-actions">
          {draft && (
            <button
              className="new-object-btn secondary"
              onClick={() => navigate(`/create-object?draft_id=${draft}`)}
            >
              {texts.dashboard.continueDraft}
            </button>
          )}

          <button
            className="new-object-btn primary"
            onClick={handleNewObjectClick}
          >
            {texts.dashboard.newObject}
          </button>
        </div>
      </div>

      {/* Dokumente */}
      <div className="tile tile-documents">
        <h2>{texts.dashboard.documents}</h2>

        <div className="documents-info">
          <p className="documents-line">
            {texts.dashboard.lastInvoice}: Februar 2025
          </p>
        </div>

        <div className="tile-divider"></div>

        <button className="documents-btn">
          {texts.dashboard.openArchive}
        </button>
      </div>

    </section>

    {/* Modals */}
    <ContactEditModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      userData={userData}
      onSave={async (updatedData) => {
        try {
          const merged = { ...(userData || {}), ...(updatedData || {}) };

          let countryName = merged.country_name || '';
          const cid = merged.country_id;
          if (cid) {
            try {
              const res = await fetch(`${process.env.REACT_APP_API_URL}/api/countries/${cid}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
              });
              if (res.ok) {
                const c = await res.json();
                countryName = c.name || c.data?.name || countryName;
              } else {
                const resAll = await fetch(`${process.env.REACT_APP_API_URL}/api/countries`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                });
                if (resAll.ok) {
                  const countries = await resAll.json();
                  const found = Array.isArray(countries)
                    ? countries.find(x => String(x.id) === String(cid))
                    : null;
                  countryName = found ? (found.name || '') : countryName;
                }
              }
            } catch (e) {
              console.warn('Fehler beim Laden von country_name', e);
            }
          }

          const street = merged.street || '';
          const house_number = merged.house_number || '';
          const addressLine1 = [street, house_number].filter(Boolean).join(' ').trim();

          const postal_code = merged.postal_code || '';
          const town = merged.town || merged.city || '';
          const pcTown = [postal_code, town].filter(Boolean).join(' ').trim();
          const addressLine2Parts = [];
          if (pcTown) addressLine2Parts.push(pcTown);
          if (countryName) addressLine2Parts.push(countryName);
          const addressLine2 = addressLine2Parts.join(', ');

          const normalized = {
            ...merged,
            addressLine1,
            addressLine2,
            country_name: countryName || merged.country_name || '',
          };

          setUserData(normalized);
          setShowModal(false);
        } catch (err) {
          console.error('Fehler beim Speichern:', err);
          setUserData(prev => ({ ...(prev || {}), ...(updatedData || {}) }));
          setShowModal(false);
        }
      }}
    />

    <ChangePasswordModal
      isOpen={showPasswordModal}
      onClose={() => setShowPasswordModal(false)}
    />

    <NewObjectModal
      isOpen={showNewObjectModal}
      onClose={() => setShowNewObjectModal(false)}
      onContinue={() => navigate(`/create-object?draft_id=${draft}`)}
      onStartNew={deleteDraftAndStartNew}
    />
  </div>
);

};

export default Dashboard;
